/**
 * useOffers — loads the offer viewer's data from the live Resales-Online feed.
 *
 * The page URL carries a `uuid` (e.g. /?uuid=C8PjRXZRpSb&rsId=5072666); the feed
 * is fetched through the app's own `agent` + `configApis`, so env / credential
 * handling is identical to the rest of the property-viewer.
 *
 * Two `uuid` formats are supported, each routed to its own endpoint (see
 * `fetchFeed`): short ids → the gateway-search endpoint; long "RP-…" ids → the
 * rv3 viewer endpoint. Both return the same response shape.
 *
 * `rsId` selects which offer is active on first load. A missing/expired link or a
 * failed request resolves to a `status` the view renders as a clear notice
 * (never silent placeholder data).
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import request from '../../agent';
import configApis from '../../configs/apis';
import { asset } from './assets';
import { mapApiProperty, mapAgency, mapSettings, overrideLayout } from './mapProperty';
import { applyLanguage } from './i18n';

// Neutral agency used only when the feed returns no agency block, so the header /
// footer still render without crashing on agency.* reads.
const EMPTY_AGENCY = {
    name: '', email: '', phone: '', website: '',
    contactSecureId: null, logo: asset('prototype/agency-logo.svg'),
};

function readParams() {
    if (typeof window === 'undefined') return { uuid: null, rsId: null, ref: null, lang: null };
    const p = new URLSearchParams(window.location.search);
    return {
        // ?ref= carries the property reference shown on the card (the agency's own
        // ref when they own the listing, else R<rsId>); ?rsId= is the original
        // numeric deep link. Either opens that property directly.
        uuid: p.get('uuid'), rsId: p.get('rsId'), ref: p.get('ref'), lang: p.get('lang'),
        // TEMPORARY layout-review overrides (see overrideLayout). Removed once the
        // layout is driven by the admin tab / API gateway.
        layoutParams: { offers: p.get('offers'), section: p.get('section') },
    };
}

// Appends a query param, choosing ? or & correctly.
function appendParam(url, key, value) {
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

// Appends the language so the API returns localized content (e.g. ?lang=es).
// `lang` is the API query-param name the gateway-search endpoint expects; adjust
// here if the contract differs (the front-end / UI param is also `lang`).
function withLang(url, lang) {
    return lang ? appendParam(url, 'lang', lang) : url;
}

// The viewer supports two id formats, each served by its own endpoint (mirrors
// the production routing):
//   - "RP-…" ids (a long, RP-prefixed uuid) → the rv3 viewer endpoint
//     (`rv3_search_url`), passed `?lang=` so the API returns content in the
//     selected language (an explicit lang wins over the link's contact language).
//   - Short ids (e.g. "zcPiMitM99e") → the gateway-search endpoint
//     (`properties_url`), with `unitdetails=1` for the per-unit breakdown.
// Both endpoints accept `?lang=` and return the SAME response shape
// ({ properties.items, agency, expiredLink, language, searchInfo }).
function isRpUuid(uuid) {
    return typeof uuid === 'string' && uuid.length > 11 && uuid.startsWith('RP-');
}

// Fetches the property feed for either id type. The language-tagged URL is tried
// first and retried without `lang` on failure, so a rejected lang param still
// returns LIVE data (language is a nice-to-have and must not break the feed).
async function fetchFeed(uuid, lang) {
    if (isRpUuid(uuid)) {
        // rv3 viewer endpoint — pass ?lang= (the API falls back to the link's
        // contact language when it's absent).
        return request.get(withLang(configApis.rv3_search_url.replace('{:id}', uuid), lang));
    }
    const baseUrl = appendParam(configApis.properties_url.replace('{:id}', uuid), 'unitdetails', '1');
    if (!lang) return request.get(baseUrl);
    try {
        return await request.get(withLang(baseUrl, lang));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[resales-landing] feed request with ?lang= failed, retrying without it:', e);
        return request.get(baseUrl);
    }
}

// Resolve deep links by printed reference, then opaque offerId, then parent rsId.
// Unit references and companion API offerIds are both U<unitId>; reference lookup
// handles those directly. The offerId fallback also supports RV3 composite IDs.
// A bare parent rsId lands on its first sibling. Printed references take priority
// so an agency reference that resembles another property's id still resolves.
export function findOfferIndex(offers, ref, rsId) {
    const byRsId = (v) => offers.findIndex((o) => String(o.rsId) === v);
    if (ref) {
        const want = String(ref).trim().toLowerCase();
        if (want) {
            const byRef = offers.findIndex((o) => String(o.reference || '').trim().toLowerCase() === want);
            if (byRef >= 0) return byRef;
            const byOfferId = offers.findIndex((o) => o.offerId != null && String(o.offerId).toLowerCase() === want);
            if (byOfferId >= 0) return byOfferId;
            const bare = want.replace(/^r/, '');
            if (/^\d+$/.test(bare)) {
                const hit = byRsId(bare);
                if (hit >= 0) return hit;
            }
        }
    }
    if (rsId) {
        const want = String(rsId).trim();
        if (want) return byRsId(want);
    }
    return -1;
}

// A property is offerable unless it has sold or gone off market. Feed statuses
// vary in spacing/case ("Off Market", "Temporarily Off Market", "Sold"), so
// compare on letters only; in-progress sales (Under Offer / Sale Agreed) stay
// visible — they carry their own image ribbon.
function isOfferable(item) {
    const key = String((item && item.RsStatus) || '').replace(/[^A-Za-z]/g, '').toLowerCase();
    return key !== 'sold' && key.indexOf('offmarket') === -1;
}

// Apply the agency's result order. 'default' keeps the order the agent selected
// when sharing (the feed order); the price options sort a COPY so the feed order
// is never mutated. Offers without a price sink to the end either way.
function sortOffers(offers, sortOrder) {
    if (sortOrder !== 'price-asc' && sortOrder !== 'price-desc') return offers;
    const dir = sortOrder === 'price-asc' ? 1 : -1;
    return offers.slice().sort((a, b) => {
        const av = a.priceValue || 0;
        const bv = b.priceValue || 0;
        if (!av && !bv) return 0;
        if (!av) return 1;   // price-less offers last, whichever direction
        if (!bv) return -1;
        return (av - bv) * dir;
    });
}

export default function useOffers() {
    const dispatch = useDispatch();
    // status: 'live' (loaded) | 'expired' (link expired) | 'error' (fetch failed,
    //         nothing to show, or no uuid). Only read once `loading` is false.
    const [state, setState] = useState(() => {
        const { uuid, layoutParams } = readParams();
        return {
            offers: [],
            agency: null,
            initialIndex: 0,
            scrollToIndex: null,
            loading: Boolean(uuid),
            // No uuid → nothing to load; the view shows the "unavailable" notice.
            status: uuid ? 'live' : 'error',
            // uuid + searchInfo are surfaced for the audit beacons (see useAudit).
            // searchInfo (SearchType / Dimension / Currency) comes from the feed.
            uuid,
            searchInfo: null,
            // Per-agency viewer customisation; defaults until the feed loads
            // (URL layout overrides applied up-front so a reviewer's ?offers=/
            // ?section= takes effect immediately, before data lands).
            settings: overrideLayout(mapSettings(null), layoutParams),
        };
    });

    useEffect(() => {
        const { uuid, rsId, ref, lang, layoutParams } = readParams();
        if (lang) applyLanguage(dispatch, { paramLang: lang });
        if (!uuid) return undefined; // nothing to fetch

        let cancelled = false;
        (async () => {
            try {
                const res = await fetchFeed(uuid, lang);
                if (cancelled) return;

                // The API can return 200 with an expired flag (valid uuid, old link).
                if (res && res.expiredLink === true) {
                    setState((s) => ({ ...s, offers: [], loading: false, status: 'expired' }));
                    return;
                }

                const feedItems = (res && res.properties && res.properties.items) || [];
                if (!feedItems.length) {
                    // request succeeded but there's nothing to show → unavailable
                    setState((s) => ({ ...s, offers: [], loading: false, status: 'error' }));
                    return;
                }
                // Sold and off-market properties are no longer offerable, so they're
                // left out of the viewer (a link shared earlier may still list them).
                const items = feedItems.filter(isOfferable);
                if (!items.length) {
                    // every property on the link has since sold / gone off market
                    setState((s) => ({ ...s, offers: [], loading: false, status: 'empty' }));
                    return;
                }

                // Per-agency viewer customisation; social handles ride on the agency
                // view-model, section/layout flags on `settings`.
                const settings = overrideLayout(mapSettings(res && res.settings), layoutParams);
                const agency = { ...(mapAgency(res && res.agency) || EMPTY_AGENCY), social: settings.social };
                const offers = sortOffers(items.map((item, i) => mapApiProperty(item, agency, i)), settings.sortOrder);

                // A ?rsId= deep link selects (and scrolls to) that offer;
                // scrollToIndex stays null without a matching rsId so the page just
                // opens at the top.
                let initialIndex = 0;
                let scrollToIndex = null;
                // ?ref= / ?rsId= deep link, matched against the SORTED offers so
                // it still lands on the right card once a price order is applied.
                const found = findOfferIndex(offers, ref, rsId);
                if (found >= 0) { initialIndex = found; scrollToIndex = found; }

                setState({
                    offers, agency, settings, initialIndex, scrollToIndex, loading: false, status: 'live',
                    uuid, searchInfo: (res && res.searchInfo) || null,
                });
                // Language is best-effort and applied AFTER the live data is
                // committed, so it can never cause a fallback. ?lang= → API → cookie.
                applyLanguage(dispatch, { paramLang: lang, apiLang: res && res.language });
            } catch (e) {
                // Fetch failed (network / 500 / parse). Show an error notice.
                // eslint-disable-next-line no-console
                console.error('[resales-landing] live data failed:', e);
                if (!cancelled) {
                    setState((s) => ({ ...s, offers: [], loading: false, status: 'error' }));
                }
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return state;
}
